import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  LogOut, Menu, X, ChevronRight, TrendingUp, 
  DollarSign, Clock, Truck, AlertCircle, Star, RotateCw,
  KeyRound, Smartphone, FileSpreadsheet, FileText, MoreVertical, Download, Sparkles,
  Tag, Upload, Play, ChevronDown, Loader2, Calendar, Settings, Mail, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../ui/input-otp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Define StatCard outside the main component to avoid re-creation on every render
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <div className="glass rounded-xl p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#A8A29E] text-sm">{title}</p>
        <p className={`text-2xl font-bold mt-1 ${color || 'text-white'}`}>{value}</p>
        {subtext && <p className="text-[#57534E] text-xs mt-1">{subtext}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color ? 'bg-opacity-10' : 'bg-[#D4AF37]/10'}`} style={{ backgroundColor: color ? `${color}20` : undefined }}>
        <Icon size={24} className={color || 'text-[#D4AF37]'} />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [newPinInput, setNewPinInput] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrData, setQrData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const menuRef = useRef(null);
  
  // Parfum Prijs states
  const [priceMenuOpen, setPriceMenuOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [priceCheckId, setPriceCheckId] = useState(null);
  const [priceCheckStatus, setPriceCheckStatus] = useState(null);
  const [latestPriceCheck, setLatestPriceCheck] = useState(null);
  const [priceCheckLoading, setPriceCheckLoading] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [manualPrices, setManualPrices] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [googleLookupLoading, setGoogleLookupLoading] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const priceMenuRef = useRef(null);
  
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadDashboardData();
  }, [adminToken]);

  const loadDashboardData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/orders?limit=5`, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setAdminMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChangePIN = async () => {
    if (pinInput.length !== 4 || newPinInput.length !== 4 || totpCode.length !== 6) {
      toast.error('Vul alle velden correct in');
      return;
    }
    
    setModalLoading(true);
    try {
      await axios.post(`${API}/admin/change-pin`, {
        current_pin: pinInput,
        new_pin: newPinInput,
        code: totpCode
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      
      toast.success('PIN succesvol gewijzigd');
      setShowPinModal(false);
      setPinInput('');
      setNewPinInput('');
      setTotpCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PIN wijzigen mislukt');
    } finally {
      setModalLoading(false);
    }
  };

  const handleReset2FA = async () => {
    if (pinInput.length !== 4) {
      toast.error('Voer je PIN in');
      return;
    }
    
    setModalLoading(true);
    try {
      const response = await axios.post(`${API}/admin/reset-own-2fa`, {
        pin: pinInput
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      
      setQrData(response.data);
      toast.success('Scan de nieuwe QR code');
    } catch (error) {
      toast.error(error.response?.data?.detail || '2FA reset mislukt');
    } finally {
      setModalLoading(false);
    }
  };

  const handleVerifyNew2FA = async () => {
    if (totpCode.length !== 6) {
      toast.error('Voer de 6-cijferige code in');
      return;
    }
    
    setModalLoading(true);
    try {
      await axios.post(`${API}/admin/verify-2fa`, {
        admin_id: qrData.admin_id,
        code: totpCode
      });
      
      toast.success('2FA opnieuw ingesteld! Log opnieuw in.');
      localStorage.removeItem('adminToken');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verificatie mislukt');
    } finally {
      setModalLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      toast.info(`${format.toUpperCase()} rapport wordt gegenereerd...`);
      
      if (format === 'json') {
        const response = await axios.get(`${API}/admin/export/revenue?format=json`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        // Download as JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `omzet_rapport_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // For Excel and PDF, use direct download
        const response = await axios.get(`${API}/admin/export/revenue?format=${format}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: 'blob'
        });
        
        const contentType = format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';
        const extension = format === 'excel' ? 'xlsx' : 'pdf';
        
        const blob = new Blob([response.data], { type: contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `omzet_rapport_${new Date().toISOString().split('T')[0]}.${extension}`;
        a.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success('Rapport gedownload');
      setAdminMenuOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export mislukt');
    }
  };

  // Parfum Prijs Functions
  const loadLatestPriceCheck = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-check/latest`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLatestPriceCheck(response.data);
      
      // Check if there's a running job and resume polling
      if (response.data && response.data.status === 'running') {
        setPriceCheckId(response.data.id);
        setPriceCheckStatus({
          status: 'running',
          processed_products: response.data.processed_products || 0,
          total_products: response.data.total_products || 0
        });
        setPriceCheckLoading(true);
        startProgressPolling(response.data.id);
      }
    } catch (error) {
      console.error('Error loading latest price check:', error);
    }
  };

  // Load cache statistics
  const loadCacheStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-check/cache/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCacheStats(response.data);
    } catch (error) {
      console.error('Error loading cache stats:', error);
    }
  };

  // Clear price cache
  const handleClearCache = async () => {
    if (!window.confirm('Weet je zeker dat je de prijs cache wilt legen? De volgende prijs check zal alle prijzen opnieuw ophalen.')) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/admin/price-check/cache`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Cache geleegd: ${response.data.deleted} items verwijderd`);
      loadCacheStats();
    } catch (error) {
      toast.error('Kon cache niet legen');
    }
  };
  
  // Polling reference to prevent multiple intervals
  const pollingRef = React.useRef(null);
  
  const startProgressPolling = (checkId) => {
    // Clear any existing interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    toast.loading('🔄 Prijs check loopt op achtergrond...', { id: 'pricecheck' });
    
    pollingRef.current = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`${API}/admin/price-check/${checkId}/status`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        
        const { status, processed_products, total_products, estimated_remaining, current_phase, phase_label, progress_percent, variants_found } = statusResponse.data;
        
        if (status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPriceCheckStatus({ status: 'completed', total_products: processed_products, variants_found });
          setPriceCheckLoading(false);
          playSuccessSound();
          const variantMsg = variants_found > 0 ? ` + ${variants_found} varianten` : '';
          toast.success(`✅ Prijs check voltooid! ${processed_products} producten${variantMsg} verwerkt.`, { id: 'pricecheck', duration: 5000 });
          loadLatestPriceCheck();
        } else if (status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPriceCheckStatus({ status: 'failed' });
          setPriceCheckLoading(false);
          toast.error('Prijs check mislukt', { id: 'pricecheck' });
        } else {
          // Update progress with phase information
          const remaining = estimated_remaining ? `~${Math.ceil(estimated_remaining / 60)} min` : '';
          const phaseIcon = current_phase === 'extra_varianten' ? '🔄' : '📊';
          toast.loading(`${phaseIcon} ${phase_label}: ${processed_products}/${total_products} (${progress_percent}%) ${remaining}`, { id: 'pricecheck' });
          setPriceCheckStatus({ 
            status: status, 
            processed_products, 
            total_products, 
            current_phase, 
            phase_label,
            progress_percent 
          });
        }
      } catch (pollError) {
        console.error('Poll error:', pollError);
        // Stop polling if job not found (404) or other error
        if (pollError.response?.status === 404) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPriceCheckStatus(null);
          setPriceCheckLoading(false);
          setPriceCheckId(null);
          toast.dismiss('pricecheck');
          console.log('Price check job not found, stopped polling');
        }
      }
    }, 3000); // Poll every 3 seconds
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const loadScheduleConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-check/schedule`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setScheduleConfig(response.data);
    } catch (error) {
      console.error('Error loading schedule config:', error);
    }
  };

  useEffect(() => {
    loadLatestPriceCheck();
    loadScheduleConfig();
    loadCacheStats();
  }, [adminToken]);

  const handleSaveSchedule = async (scheduleType, time, dayOfWeek = null) => {
    try {
      await axios.post(`${API}/admin/price-check/schedule`, {
        schedule_type: scheduleType,
        time: time,
        day_of_week: dayOfWeek,
        enabled: true
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Schedule opgeslagen');
      loadScheduleConfig();
      setShowScheduleModal(false);
    } catch (error) {
      toast.error('Schedule opslaan mislukt');
    }
  };

  const handleToggleSchedule = async (enabled) => {
    try {
      await axios.post(`${API}/admin/price-check/schedule/toggle`, {
        enabled: enabled
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(enabled ? 'Automatische check ingeschakeld' : 'Automatische check uitgeschakeld');
      loadScheduleConfig();
    } catch (error) {
      toast.error('Fout bij wijzigen schedule');
    }
  };

  // Close price menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (priceMenuRef.current && !priceMenuRef.current.contains(event.target)) {
        setPriceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownloadTemplate = async () => {
    try {
      toast.loading('Template downloaden...', { id: 'template' });
      const response = await axios.get(`${API}/admin/price-check/template`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Parfum_Prices_Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Template gedownload', { id: 'template' });
      setPriceMenuOpen(false);
    } catch (error) {
      toast.error('Template download mislukt', { id: 'template' });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Alleen Excel bestanden (.xlsx, .xls) zijn toegestaan');
      return;
    }
    
    setPriceCheckLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/price-check/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadedFile(file.name);
      setPriceCheckId(response.data.id);
      setPriceCheckStatus({
        status: 'uploaded',
        total_products: response.data.total_products
      });
      
      toast.success(`${response.data.total_products} producten geladen. Klik op "Start Prijs Check" om te beginnen.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload mislukt');
    } finally {
      setPriceCheckLoading(false);
    }
  };

  // Function to play success sound
  const playSuccessSound = () => {
    try {
      // Create audio context for notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for a pleasant chime sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification tone sequence
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Could not play sound:', e);
    }
  };

  const handleStartPriceCheck = async () => {
    if (!priceCheckId) {
      toast.error('Upload eerst een bestand');
      return;
    }
    
    setPriceCheckLoading(true);
    setPriceCheckStatus(prev => ({ ...prev, status: 'running' }));
    
    try {
      // Start the price check as a BACKGROUND job (doesn't wait for completion)
      await axios.post(`${API}/admin/price-check/${priceCheckId}/start-background`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        timeout: 10000 // 10 second timeout just to start the job
      });
      
      // Start polling for progress (this persists even if page refreshes)
      startProgressPolling(priceCheckId);
      
    } catch (error) {
      setPriceCheckStatus(prev => ({ ...prev, status: 'failed' }));
      setPriceCheckLoading(false);
      toast.error(error.response?.data?.detail || 'Kon prijs check niet starten', { id: 'pricecheck' });
    }
  };

  const handleDownloadResult = async () => {
    const checkId = priceCheckId || latestPriceCheck?.id;
    if (!checkId) {
      toast.error('Geen resultaten beschikbaar');
      return;
    }
    
    try {
      toast.loading('Resultaten downloaden...', { id: 'result' });
      const response = await axios.get(`${API}/admin/price-check/${checkId}/download`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Parfum_Prices_${checkId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Resultaten gedownload', { id: 'result' });
      setPriceMenuOpen(false);
    } catch (error) {
      toast.error('Download mislukt', { id: 'result' });
    }
  };

  // Load manual prices
  const loadManualPrices = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-check/manual-prices`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setManualPrices(response.data.prices || []);
    } catch (error) {
      console.error('Error loading manual prices:', error);
    }
  };

  // Load tracking data
  const loadTrackingData = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-check/all-tracking`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTrackingData(response.data.tracking || []);
    } catch (error) {
      console.error('Error loading tracking data:', error);
    }
  };

  // Google Shopping lookup
  const handleGoogleLookup = async (productName) => {
    setGoogleLookupLoading(true);
    try {
      const response = await axios.post(`${API}/admin/price-check/google-lookup`, 
        { product_name: productName },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      if (response.data.found && response.data.prices?.length > 0) {
        playSuccessSound();
        toast.success(`Prijzen gevonden: €${response.data.prices.join(', €')}`);
        return response.data.prices;
      } else {
        toast.info('Geen prijzen gevonden via Google');
        return [];
      }
    } catch (error) {
      toast.error('Google lookup mislukt');
      return [];
    } finally {
      setGoogleLookupLoading(false);
    }
  };

  // Export full report
  const handleExportFullReport = async () => {
    try {
      toast.loading('Volledig rapport exporteren...', { id: 'full-report' });
      const response = await axios.post(`${API}/admin/price-check/export-full`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ZuiverParfum_Volledig_Rapport.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      
      playSuccessSound();
      toast.success('Volledig rapport gedownload', { id: 'full-report' });
    } catch (error) {
      toast.error('Export mislukt', { id: 'full-report' });
    }
  };

  // Export manual prices
  const handleExportManualPrices = async () => {
    try {
      toast.loading('Handmatige prijzen exporteren...', { id: 'manual-export' });
      const response = await axios.post(`${API}/admin/price-check/export-manual`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Parfum_Prices_Manual.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Export voltooid', { id: 'manual-export' });
    } catch (error) {
      toast.error('Export mislukt', { id: 'manual-export' });
    }
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Bestellingen', icon: ShoppingCart },
    { path: '/admin/products', label: 'Producten', icon: Package },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/360-views', label: '360° Views', icon: RotateCw },
    { path: '/admin/customers', label: 'Klanten', icon: Users },
    { path: '/admin/suggestions', label: 'Suggesties', icon: Sparkles },
    { path: '/admin/managers', label: 'Beheerders', icon: KeyRound },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      processing: 'bg-blue-500/10 text-blue-500',
      shipped: 'bg-purple-500/10 text-purple-500',
      delivered: 'bg-green-500/10 text-green-500',
      cancelled: 'bg-red-500/10 text-red-500'
    };
    return colors[status] || 'bg-gray-500/10 text-gray-500';
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 glass-strong transform transition-transform duration-300
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#2C1B18]">
            <h1 className="font-heading text-xl">
              <span className="gold-text">Zuiver</span>
              <span className="text-white">Parfum</span>
            </h1>
            <p className="text-[#57534E] text-xs mt-1">Admin Portal</p>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-[#A8A29E] hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-[#2C1B18]">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 w-full transition-colors"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="glass-strong sticky top-0 z-30 px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-white"
            >
              <Menu size={24} />
            </button>
            <h2 className="font-heading text-xl text-white">Dashboard</h2>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Parfum Prijs Dropdown */}
            <div className="relative" ref={priceMenuRef}>
              <button
                onClick={() => setPriceMenuOpen(!priceMenuOpen)}
                className="hidden md:flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
                title="Parfum Prijzen"
              >
                <Tag size={18} />
                <span className="hidden lg:inline">Parfum Prijs</span>
                <ChevronDown size={16} className={`transition-transform ${priceMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {priceMenuOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-[#1C1917] rounded-xl shadow-2xl border border-[#2C1B18] overflow-hidden z-50">
                  <div className="p-3 border-b border-[#2C1B18] bg-[#1C1917]">
                    <p className="text-white font-medium flex items-center gap-2">
                      <Tag size={18} className="text-purple-400" />
                      Parfum Prijs Check
                    </p>
                  </div>
                  <div className="py-2 bg-[#1C1917]">
                    {/* Download Template */}
                    <button
                      onClick={handleDownloadTemplate}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A8A29E] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <Download size={18} />
                      Download Excel Template
                    </button>
                    
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    
                    {/* Upload Section */}
                    <div className="px-4 py-3">
                      <label className="w-full flex items-center gap-3 cursor-pointer text-[#A8A29E] hover:text-white transition-colors">
                        <Upload size={18} />
                        <span>Import Parfum Prices.xlsx</span>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </label>
                      
                      {uploadedFile && (
                        <div className="mt-2 p-2 bg-purple-500/10 rounded-lg">
                          <p className="text-purple-400 text-sm truncate">{uploadedFile}</p>
                          {priceCheckStatus && (
                            <p className="text-[#A8A29E] text-xs mt-1">
                              {priceCheckStatus.total_products} producten geladen
                            </p>
                          )}
                        </div>
                      )}
                      
                      {priceCheckStatus?.status === 'uploaded' && (
                        <button
                          onClick={handleStartPriceCheck}
                          disabled={priceCheckLoading}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 rounded-lg text-white text-sm transition-colors"
                        >
                          {priceCheckLoading ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Play size={18} />
                          )}
                          Start Prijs Check
                        </button>
                      )}
                      
                      {(priceCheckStatus?.status === 'running' || priceCheckStatus?.status === 'processing_variants') && (
                        <div className="mt-3 space-y-2">
                          {/* Phase 1: Parfum Prices */}
                          {priceCheckStatus?.status === 'running' && (
                            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-blue-400 text-sm font-medium flex items-center gap-2">
                                  <Loader2 size={14} className="animate-spin" />
                                  📊 Parfum Prices
                                </span>
                                <span className="text-blue-300 text-xs">
                                  {priceCheckStatus.progress_percent || 0}%
                                </span>
                              </div>
                              <div className="w-full bg-blue-900/30 rounded-full h-2">
                                <div 
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${priceCheckStatus.progress_percent || 0}%` }}
                                ></div>
                              </div>
                              <p className="text-blue-300/70 text-xs mt-1">
                                {priceCheckStatus.processed_products}/{priceCheckStatus.total_products} producten
                              </p>
                            </div>
                          )}
                          
                          {/* Phase 1 complete, waiting for Phase 2 */}
                          {priceCheckStatus?.status === 'running' && priceCheckStatus.progress_percent >= 100 && (
                            <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-yellow-400 text-xs flex items-center gap-2">
                                <Loader2 size={12} className="animate-spin" />
                                Wacht op Extra fles varianten...
                              </p>
                            </div>
                          )}
                          
                          {/* Phase 2: Extra fles varianten */}
                          {priceCheckStatus?.status === 'processing_variants' && (
                            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-purple-400 text-sm font-medium flex items-center gap-2">
                                  <Loader2 size={14} className="animate-spin" />
                                  🔄 Extra fles varianten
                                </span>
                                <span className="text-purple-300 text-xs">
                                  {priceCheckStatus.progress_percent || 0}%
                                </span>
                              </div>
                              <div className="w-full bg-purple-900/30 rounded-full h-2">
                                <div 
                                  className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${priceCheckStatus.progress_percent || 0}%` }}
                                ></div>
                              </div>
                              <p className="text-purple-300/70 text-xs mt-1">
                                {priceCheckStatus.processed_products}/{priceCheckStatus.total_products} varianten
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {priceCheckStatus?.status === 'completed' && (
                        <button
                          onClick={handleDownloadResult}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#D4AF37] hover:bg-[#E5C048] rounded-lg text-black text-sm font-medium transition-colors"
                        >
                          <Download size={18} />
                          Download Resultaten
                        </button>
                      )}
                    </div>
                    
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    
                    {/* Latest Price Check */}
                    <div className="px-4 py-3">
                      <p className="text-[#57534E] text-xs uppercase tracking-wider mb-2">Laatste Prijs Check</p>
                      {latestPriceCheck?.has_results ? (
                        <div>
                          <p className="text-[#A8A29E] text-sm">
                            {latestPriceCheck.total_products} producten
                          </p>
                          <p className="text-[#57534E] text-xs">
                            {new Date(latestPriceCheck.completed_at).toLocaleString('nl-NL')}
                          </p>
                          <button
                            onClick={handleDownloadResult}
                            className="mt-2 flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition-colors"
                          >
                            <Download size={14} />
                            Download Laatste Versie
                          </button>
                        </div>
                      ) : (
                        <p className="text-[#57534E] text-sm">Geen resultaten beschikbaar</p>
                      )}
                    </div>
                    
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    
                    {/* Price Cache Info */}
                    <div className="px-4 py-3">
                      <p className="text-[#57534E] text-xs uppercase tracking-wider mb-2">Prijs Cache</p>
                      {cacheStats ? (
                        <div>
                          <p className="text-[#A8A29E] text-sm">
                            {cacheStats.total_cached_products} producten gecached
                          </p>
                          <p className="text-[#57534E] text-xs">
                            TTL: {cacheStats.cache_ttl_hours} uur
                          </p>
                          <button
                            onClick={handleClearCache}
                            className="mt-2 flex items-center gap-2 text-orange-400 hover:text-orange-300 text-xs transition-colors"
                          >
                            <Trash2 size={12} />
                            Cache Legen
                          </button>
                        </div>
                      ) : (
                        <p className="text-[#57534E] text-sm">Cache leeg</p>
                      )}
                    </div>
                    
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    
                    {/* Automatische Schedule */}
                    <div className="px-4 py-3">
                      <p className="text-[#57534E] text-xs uppercase tracking-wider mb-2">Automatische Check</p>
                      {scheduleConfig?.enabled ? (
                        <div>
                          <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                            <Calendar size={14} />
                            {scheduleConfig.schedule_type === 'daily' 
                              ? `Dagelijks om ${scheduleConfig.time}`
                              : `Wekelijks op ${['Zo','Ma','Di','Wo','Do','Vr','Za'][scheduleConfig.day_of_week]} om ${scheduleConfig.time}`
                            }
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowScheduleModal(true)}
                              className="flex items-center gap-1 text-[#A8A29E] hover:text-white text-xs transition-colors"
                            >
                              <Settings size={12} />
                              Wijzigen
                            </button>
                            <button
                              onClick={() => handleToggleSchedule(false)}
                              className="flex items-center gap-1 text-red-400 hover:text-red-300 text-xs transition-colors"
                            >
                              Uitschakelen
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowScheduleModal(true)}
                          className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition-colors"
                        >
                          <Calendar size={14} />
                          Schedule Instellen
                        </button>
                      )}
                    </div>
                    
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    
                    {/* Handmatige Invoer & Tracking */}
                    <div className="px-4 py-3">
                      <p className="text-[#57534E] text-xs uppercase tracking-wider mb-2">Extra Opties</p>
                      
                      <button
                        onClick={handleExportFullReport}
                        className="w-full flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C048] text-sm py-2 transition-colors"
                      >
                        <FileSpreadsheet size={16} />
                        Volledig Rapport Downloaden
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Export Buttons */}
            <button
              onClick={() => handleExport('excel')}
              className="hidden md:flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors"
              title="Export naar Excel"
            >
              <FileSpreadsheet size={18} />
              <span className="hidden lg:inline">Export</span>
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="hidden md:flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
              title="Export naar PDF"
            >
              <FileText size={18} />
              <span className="hidden lg:inline">PDF</span>
            </button>
            
            {/* Admin Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37] hover:bg-[#E5C048] rounded-lg text-black font-medium transition-colors"
              >
                <span>Master Admin</span>
                <MoreVertical size={18} />
              </button>
              
              {adminMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-[#1C1917] rounded-xl shadow-2xl border border-[#2C1B18] overflow-hidden z-50">
                  <div className="p-3 border-b border-[#2C1B18] bg-[#1C1917]">
                    <p className="text-white font-medium">Master Admin Menu</p>
                  </div>
                  <div className="py-2 bg-[#1C1917]">
                    <button
                      onClick={() => { setShowPinModal(true); setAdminMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A8A29E] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <KeyRound size={18} />
                      PIN Wijzigen
                    </button>
                    <button
                      onClick={() => { setShow2FAModal(true); setAdminMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A8A29E] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <Smartphone size={18} />
                      2FA Opnieuw Instellen
                    </button>
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    <button
                      onClick={() => handleExport('excel')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A8A29E] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <FileSpreadsheet size={18} />
                      Omzet Export (Excel)
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A8A29E] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <FileText size={18} />
                      Omzet Export (PDF)
                    </button>
                    <div className="border-t border-[#2C1B18] my-2"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut size={18} />
                      Uitloggen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* PIN Wijzigen Modal */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="glass rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-white text-xl font-medium mb-6 text-center">PIN Wijzigen</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-[#A8A29E] text-sm mb-2">Huidige PIN</label>
                  <InputOTP maxLength={4} value={pinInput} onChange={setPinInput} className="justify-center">
                    <InputOTPGroup>
                      {[0,1,2,3].map(i => (
                        <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                <div>
                  <label className="block text-[#A8A29E] text-sm mb-2">Nieuwe PIN</label>
                  <InputOTP maxLength={4} value={newPinInput} onChange={setNewPinInput} className="justify-center">
                    <InputOTPGroup>
                      {[0,1,2,3].map(i => (
                        <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                <div>
                  <label className="block text-[#A8A29E] text-sm mb-2">2FA Code</label>
                  <InputOTP maxLength={6} value={totpCode} onChange={setTotpCode} className="justify-center">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setShowPinModal(false); setPinInput(''); setNewPinInput(''); setTotpCode(''); }}
                  className="flex-1 px-4 py-3 border border-[#2C1B18] rounded-lg text-[#A8A29E] hover:text-white transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleChangePIN}
                  disabled={modalLoading}
                  className="flex-1 btn-primary flex items-center justify-center"
                >
                  {modalLoading ? <div className="spinner"></div> : 'Wijzigen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2FA Reset Modal */}
        {show2FAModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="glass rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-white text-xl font-medium mb-6 text-center">2FA Opnieuw Instellen</h3>
              
              {!qrData ? (
                <div className="space-y-4">
                  <p className="text-[#A8A29E] text-sm text-center">
                    Voer je PIN in om een nieuwe QR code te genereren voor Microsoft Authenticator.
                  </p>
                  <div>
                    <label className="block text-[#A8A29E] text-sm mb-2">PIN</label>
                    <InputOTP maxLength={4} value={pinInput} onChange={setPinInput} className="justify-center">
                      <InputOTPGroup>
                        {[0,1,2,3].map(i => (
                          <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => { setShow2FAModal(false); setPinInput(''); }}
                      className="flex-1 px-4 py-3 border border-[#2C1B18] rounded-lg text-[#A8A29E] hover:text-white transition-colors"
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={handleReset2FA}
                      disabled={modalLoading}
                      className="flex-1 btn-primary flex items-center justify-center"
                    >
                      {modalLoading ? <div className="spinner"></div> : 'Genereer QR'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 text-center">
                  <p className="text-[#A8A29E] text-sm">
                    Scan deze code met Microsoft Authenticator
                  </p>
                  <img src={qrData.qr_code} alt="QR Code" className="mx-auto w-48 h-48 bg-white p-2 rounded-lg" />
                  <p className="text-[#57534E] text-xs">Secret: {qrData.secret}</p>
                  
                  <div>
                    <label className="block text-[#A8A29E] text-sm mb-2">Voer code in om te verifiëren</label>
                    <InputOTP maxLength={6} value={totpCode} onChange={setTotpCode} className="justify-center">
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => (
                          <InputOTPSlot key={i} index={i} className="bg-[#1C1917] border-[#2C1B18] text-white" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  
                  <button
                    onClick={handleVerifyNew2FA}
                    disabled={modalLoading}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    {modalLoading ? <div className="spinner"></div> : 'Verifiëren & Opslaan'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        <div className="p-4 md:p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Revenue" 
                  value={`€${stats?.total_revenue?.toFixed(2) || '0.00'}`}
                  icon={DollarSign}
                  color="text-green-500"
                />
                <StatCard 
                  title="Total Orders" 
                  value={stats?.total_orders || 0}
                  icon={ShoppingCart}
                />
                <StatCard 
                  title="Pending Orders" 
                  value={stats?.pending_orders || 0}
                  icon={Clock}
                  color="text-yellow-500"
                  subtext={`${stats?.processing_orders || 0} processing`}
                />
                <StatCard 
                  title="Customers" 
                  value={stats?.total_customers || 0}
                  icon={Users}
                />
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="glass rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-medium">Order Status Overview</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <span className="text-[#A8A29E]">Pending</span>
                      </div>
                      <span className="text-white font-medium">{stats?.pending_orders || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-[#A8A29E]">Processing</span>
                      </div>
                      <span className="text-white font-medium">{stats?.processing_orders || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                        <span className="text-[#A8A29E]">Shipped</span>
                      </div>
                      <span className="text-white font-medium">{stats?.shipped_orders || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="glass rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-medium">Products</h3>
                    <Link to="/admin/products" className="text-[#D4AF37] text-sm hover:underline">
                      Manage →
                    </Link>
                  </div>
                  <div className="flex items-center gap-4">
                    <Package size={48} className="text-[#D4AF37]" />
                    <div>
                      <p className="text-3xl font-bold text-white">{stats?.total_products || 0}</p>
                      <p className="text-[#A8A29E] text-sm">Active products</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium">Recent Orders</h3>
                  <Link to="/admin/orders" className="text-[#D4AF37] text-sm hover:underline">
                    View All →
                  </Link>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-[#A8A29E] text-sm border-b border-[#2C1B18]">
                        <th className="text-left py-3 px-2">Order ID</th>
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-right py-3 px-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-[#57534E]">
                            No orders yet
                          </td>
                        </tr>
                      ) : (
                        recentOrders.map((order) => (
                          <tr key={order.id} className="border-b border-[#2C1B18]/50 hover:bg-white/5">
                            <td className="py-3 px-2 text-white">#{order.id.slice(0, 8)}</td>
                            <td className="py-3 px-2 text-[#A8A29E]">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.status)}`}>
                                {order.status}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-right text-[#D4AF37] font-medium">
                              €{order.total.toFixed(2)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      
      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1C1917] rounded-2xl p-6 max-w-md w-full border border-[#2C1B18]">
            <h3 className="text-white text-xl font-medium mb-6 text-center flex items-center justify-center gap-2">
              <Calendar className="text-purple-400" />
              Automatische Prijs Check
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Type</label>
                <select 
                  id="scheduleType"
                  defaultValue={scheduleConfig?.schedule_type || 'daily'}
                  className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                >
                  <option value="daily">Dagelijks</option>
                  <option value="weekly">Wekelijks</option>
                </select>
              </div>
              
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Tijd</label>
                <input
                  id="scheduleTime"
                  type="time"
                  defaultValue={scheduleConfig?.time || '06:00'}
                  className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                />
              </div>
              
              <div id="dayOfWeekContainer" className="hidden">
                <label className="block text-[#A8A29E] text-sm mb-2">Dag van de week</label>
                <select 
                  id="scheduleDayOfWeek"
                  defaultValue={scheduleConfig?.day_of_week || 1}
                  className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                >
                  <option value={0}>Zondag</option>
                  <option value={1}>Maandag</option>
                  <option value={2}>Dinsdag</option>
                  <option value={3}>Woensdag</option>
                  <option value={4}>Donderdag</option>
                  <option value={5}>Vrijdag</option>
                  <option value={6}>Zaterdag</option>
                </select>
              </div>
              
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <div className="flex items-center gap-2 text-purple-400 text-sm">
                  <Mail size={16} />
                  Email notificatie naar: e.grant@live.nl
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 px-4 py-3 border border-[#2C1B18] text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={() => {
                  const type = document.getElementById('scheduleType').value;
                  const time = document.getElementById('scheduleTime').value;
                  const dayOfWeek = type === 'weekly' ? parseInt(document.getElementById('scheduleDayOfWeek').value) : null;
                  handleSaveSchedule(type, time, dayOfWeek);
                }}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Manual Price Entry Modal */}
      {showManualEntryModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-[#1C1917] rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto border border-[#2C1B18]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-xl font-medium flex items-center gap-2">
                <FileText className="text-purple-400" />
                Handmatige Prijsinvoer
              </h3>
              <button onClick={() => setShowManualEntryModal(false)} className="text-[#57534E] hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-blue-500/10 rounded-lg">
              <p className="text-blue-400 text-sm">
                💡 Voer handmatig prijzen in wanneer automatische scraping niet werkt. 
                Klik op "Google Zoeken" om prijzen via Google op te halen.
              </p>
            </div>
            
            <form id="manualPriceForm" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const entries = [];
              
              // Get all unique product names from the form
              const productInputs = document.querySelectorAll('input[name^="product_"]');
              const productNames = new Set();
              productInputs.forEach(input => {
                const name = input.name.replace('product_', '').split('_')[0];
                productNames.add(name);
              });
              
              // For simplicity, collect the single entry
              const entry = {
                product_name: formData.get('product_name') || '',
                ici_paris: formData.get('ici_paris') || '',
                deloox: formData.get('deloox') || '',
                douglas: formData.get('douglas') || '',
                notino: formData.get('notino') || '',
                parfumerie_zuiver: formData.get('parfumerie_zuiver') || '',
                reviews_count: parseInt(formData.get('reviews_count')) || 0,
                is_bestseller: formData.get('is_bestseller') === 'on',
                is_out_of_stock: formData.get('is_out_of_stock') === 'on'
              };
              
              if (entry.product_name) {
                entries.push(entry);
                
                try {
                  await axios.post(`${API}/admin/price-check/manual-entry`, entries, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                  });
                  playSuccessSound();
                  toast.success('Prijzen opgeslagen!');
                  loadManualPrices();
                  e.target.reset();
                } catch (error) {
                  toast.error('Opslaan mislukt');
                }
              }
            }}>
              <div className="grid gap-4">
                <div>
                  <label className="block text-[#A8A29E] text-sm mb-2">Parfum Naam *</label>
                  <div className="flex gap-2">
                    <input
                      name="product_name"
                      type="text"
                      placeholder="bijv. Tom Ford Oud Wood 100ml"
                      className="flex-1 bg-[#121212] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        const nameInput = document.querySelector('input[name="product_name"]');
                        if (nameInput?.value) {
                          const prices = await handleGoogleLookup(nameInput.value);
                          if (prices.length > 0) {
                            // Auto-fill first price found
                            const iciInput = document.querySelector('input[name="ici_paris"]');
                            if (iciInput) iciInput.value = prices[0];
                          }
                        }
                      }}
                      disabled={googleLookupLoading}
                      className="px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg flex items-center gap-2"
                    >
                      {googleLookupLoading ? <Loader2 size={18} className="animate-spin" /> : '🔍'} Google
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">ICI Paris XL (€)</label>
                    <input name="ici_paris" type="text" placeholder="89,95" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">Deloox (€)</label>
                    <input name="deloox" type="text" placeholder="79,95" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">Douglas (€)</label>
                    <input name="douglas" type="text" placeholder="94,00" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">Notino (€)</label>
                    <input name="notino" type="text" placeholder="75,50" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">Parfumerie Zuiver (€)</label>
                    <input name="parfumerie_zuiver" type="text" placeholder="69,95" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[#A8A29E] text-xs mb-1">Aantal Reviews</label>
                    <input name="reviews_count" type="number" placeholder="0" className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <input name="is_bestseller" type="checkbox" id="is_bestseller" className="w-4 h-4" />
                    <label htmlFor="is_bestseller" className="text-[#A8A29E] text-sm">Bestseller</label>
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <input name="is_out_of_stock" type="checkbox" id="is_out_of_stock" className="w-4 h-4" />
                    <label htmlFor="is_out_of_stock" className="text-[#A8A29E] text-sm">Uitverkocht</label>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-[#D4AF37] hover:bg-[#E5C048] text-black rounded-lg font-medium transition-colors"
                  >
                    Opslaan
                  </button>
                  <button
                    type="button"
                    onClick={handleExportManualPrices}
                    className="px-4 py-3 border border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/10 transition-colors"
                  >
                    Exporteer Alles
                  </button>
                </div>
              </div>
            </form>
            
            {/* Saved Prices Table */}
            {manualPrices.length > 0 && (
              <div className="mt-6">
                <h4 className="text-white font-medium mb-3">Opgeslagen Prijzen ({manualPrices.length})</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[#57534E] text-left">
                        <th className="pb-2">Parfum</th>
                        <th className="pb-2">ICI</th>
                        <th className="pb-2">Deloox</th>
                        <th className="pb-2">Douglas</th>
                        <th className="pb-2">Notino</th>
                        <th className="pb-2">Reviews</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-[#A8A29E]">
                      {manualPrices.slice(0, 10).map((price, idx) => (
                        <tr key={idx} className="border-t border-[#2C1B18]">
                          <td className="py-2 text-white">{price.product_name}</td>
                          <td className="py-2">{price.ici_paris || '-'}</td>
                          <td className="py-2">{price.deloox || '-'}</td>
                          <td className="py-2">{price.douglas || '-'}</td>
                          <td className="py-2">{price.notino || '-'}</td>
                          <td className="py-2">{price.reviews_count || 0}</td>
                          <td className="py-2">
                            {price.is_bestseller && <span className="text-yellow-400 mr-1">⭐</span>}
                            {price.is_out_of_stock && <span className="text-red-400">Uitverkocht</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Tracking Modal (Reviews, Bestseller, Stock) */}
      {showTrackingModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-[#1C1917] rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto border border-[#2C1B18]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-xl font-medium flex items-center gap-2">
                <TrendingUp className="text-green-400" />
                Reviews & Voorraad Tracking
              </h3>
              <button onClick={() => setShowTrackingModal(false)} className="text-[#57534E] hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4 grid grid-cols-3 gap-3">
              <div className="p-4 bg-[#121212] rounded-lg text-center">
                <p className="text-2xl font-bold text-[#D4AF37]">{trackingData.length}</p>
                <p className="text-[#57534E] text-sm">Producten Tracked</p>
              </div>
              <div className="p-4 bg-[#121212] rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-400">{trackingData.filter(t => t.is_bestseller).length}</p>
                <p className="text-[#57534E] text-sm">Bestsellers</p>
              </div>
              <div className="p-4 bg-[#121212] rounded-lg text-center">
                <p className="text-2xl font-bold text-red-400">{trackingData.filter(t => t.is_out_of_stock).length}</p>
                <p className="text-[#57534E] text-sm">Uitverkocht</p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-purple-500/10 rounded-lg">
              <p className="text-purple-400 text-sm">
                📊 Track populariteit en voorraadstatus van producten bij concurrenten.
                Deze data kan u helpen bij prijsstrategieën.
              </p>
            </div>
            
            {/* Quick Add Tracking */}
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              
              const entry = {
                product_name: formData.get('track_product'),
                website: formData.get('track_website'),
                reviews_count: parseInt(formData.get('track_reviews')) || 0,
                rating: parseFloat(formData.get('track_rating')) || 0,
                is_bestseller: formData.get('track_bestseller') === 'on',
                is_out_of_stock: formData.get('track_stock') === 'on'
              };
              
              if (entry.product_name && entry.website) {
                try {
                  await axios.post(`${API}/admin/price-check/tracking`, entry, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                  });
                  toast.success('Tracking opgeslagen!');
                  loadTrackingData();
                  e.target.reset();
                } catch (error) {
                  toast.error('Opslaan mislukt');
                }
              }
            }} className="mb-6 p-4 bg-[#121212] rounded-lg">
              <h4 className="text-white font-medium mb-3">Nieuw Tracking Toevoegen</h4>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div className="col-span-2">
                  <input name="track_product" type="text" placeholder="Parfum naam" className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm" required />
                </div>
                <div>
                  <select name="track_website" className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm" required>
                    <option value="">Website</option>
                    <option value="ici_paris">ICI Paris XL</option>
                    <option value="douglas">Douglas</option>
                    <option value="notino">Notino</option>
                    <option value="deloox">Deloox</option>
                  </select>
                </div>
                <div>
                  <input name="track_reviews" type="number" placeholder="Reviews" className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1 text-[#A8A29E] text-xs">
                    <input name="track_bestseller" type="checkbox" /> ⭐
                  </label>
                  <label className="flex items-center gap-1 text-[#A8A29E] text-xs">
                    <input name="track_stock" type="checkbox" /> ❌
                  </label>
                </div>
                <div>
                  <button type="submit" className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm">Toevoegen</button>
                </div>
              </div>
            </form>
            
            {/* Tracking Table */}
            {trackingData.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#57534E] text-left">
                      <th className="pb-2">Parfum</th>
                      <th className="pb-2">Website</th>
                      <th className="pb-2">Reviews</th>
                      <th className="pb-2">Bestseller</th>
                      <th className="pb-2">Voorraad</th>
                      <th className="pb-2">Update</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#A8A29E]">
                    {trackingData.map((track, idx) => (
                      <tr key={idx} className="border-t border-[#2C1B18]">
                        <td className="py-2 text-white">{track.product_name}</td>
                        <td className="py-2">{track.website}</td>
                        <td className="py-2">{track.reviews_count || 0}</td>
                        <td className="py-2">{track.is_bestseller ? <span className="text-yellow-400">⭐ Ja</span> : 'Nee'}</td>
                        <td className="py-2">{track.is_out_of_stock ? <span className="text-red-400">Uitverkocht</span> : <span className="text-green-400">Op voorraad</span>}</td>
                        <td className="py-2 text-[#57534E] text-xs">{track.updated_at ? new Date(track.updated_at).toLocaleDateString('nl-NL') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTrackingModal(false)}
                className="flex-1 px-4 py-3 border border-[#2C1B18] text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Sluiten
              </button>
              <button
                onClick={handleExportFullReport}
                className="flex-1 px-4 py-3 bg-[#D4AF37] hover:bg-[#E5C048] text-black rounded-lg font-medium transition-colors"
              >
                Exporteer Volledig Rapport
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
